import { FacebookIcon, InstagramIcon } from '@/components/icons/platform-icons';
import { ActionLogDateRangeFilter } from './action-log-date-range';
import { ActionLogMultiSelect, type ActionLogFilterOption } from './action-log-multi-select';
import {
	DECISION_DESCRIPTIONS,
	DECISION_LABELS,
	FILTERABLE_DECISIONS,
	SOCIAL_LOG_ACTION_TYPES,
	activeActionLogFilterCount,
	type ActionLogDateRange,
	type ActionLogFilters
} from '../action-log-constants';
import { SOCIAL_ACTION_LABELS, channelLabel, channelSubLabel } from '../constants';
import type { FilterableChannel } from '../constants';
import type { SocialActionDecision, SocialActionType } from '../types';

/**
 * The controls above the log.
 *
 * Decision is NOT pre-set to `fired`. The question people arrive with is usually "why did nothing
 * happen?", and a decision filter that defaults to the one decision meaning something DID happen
 * hides exactly the rows that answer it.
 */

interface ActionLogFilterBarProps {
	filters: ActionLogFilters;
	channels: FilterableChannel[];
	onRangeChange: (range: ActionLogDateRange) => void;
	onDecisionsChange: (decisions: SocialActionDecision[]) => void;
	onActionTypesChange: (actionTypes: SocialActionType[]) => void;
	onChannelIdsChange: (channelIds: string[]) => void;
}

const DECISION_OPTIONS: ActionLogFilterOption[] = FILTERABLE_DECISIONS.map((decision) => ({
	value: decision,
	label: DECISION_LABELS[decision],
	hint: DECISION_DESCRIPTIONS[decision]
}));

const ACTION_OPTIONS: ActionLogFilterOption[] = SOCIAL_LOG_ACTION_TYPES.map((actionType) => ({
	value: actionType,
	label: SOCIAL_ACTION_LABELS[actionType]
}));

const toChannelOptions = (channels: FilterableChannel[]): ActionLogFilterOption[] =>
	channels.map((channel) => ({
		value: channel.channelId,
		label: channelLabel(channel),
		hint: channelSubLabel(channel),
		icon:
			channel.platform === 'facebook' ? (
				<FacebookIcon className='mt-0.5 size-3.5 shrink-0' />
			) : (
				<InstagramIcon className='mt-0.5 size-3.5 shrink-0' />
			)
	}));

export const ActionLogFilterBar = ({
	filters,
	channels,
	onRangeChange,
	onDecisionsChange,
	onActionTypesChange,
	onChannelIdsChange
}: ActionLogFilterBarProps) => {
	const activeCount = activeActionLogFilterCount(filters);

	// Four filter pills at 10px, and nothing else — the search moved up into the page header
	// (Figma 4768:14486), which is what stopped its position drifting with the filter count.
	return (
		<div className='flex flex-wrap items-center gap-2.5 px-4 pb-4 pt-3'>
			<ActionLogDateRangeFilter value={filters.range} onChange={onRangeChange} />

			<ActionLogMultiSelect
				label='Decision'
				options={DECISION_OPTIONS}
				value={filters.decisions}
				onChange={(next) => onDecisionsChange(next as SocialActionDecision[])}
			/>

			<ActionLogMultiSelect
				label='Action'
				options={ACTION_OPTIONS}
				value={filters.actionTypes}
				onChange={(next) => onActionTypesChange(next as SocialActionType[])}
			/>

			<ActionLogMultiSelect
				label='Channel'
				options={toChannelOptions(channels)}
				value={filters.channelIds}
				onChange={onChannelIdsChange}
				emptyMessage='No Meta accounts connected'
			/>

			{activeCount > 0 && (
				<span className='ml-auto text-xs text-muted-foreground'>
					{activeCount} {activeCount === 1 ? 'filter' : 'filters'} applied
				</span>
			)}
		</div>
	);
};
